"use client";

/**
 * AlertPublicViewContext — the single source of truth for the non-owner
 * experience on /alerts/{id}.
 *
 * Before this rewrite the context was called AlertPaywallContext and only
 * knew how to "open a paywall". That collapsed two different product
 * concerns (viewing the LIVE dashboard + receiving EMAILS) into one gate,
 * which produced confused UX: paid users were being asked to subscribe
 * before they could see their own live data. Fixed by this context.
 *
 * Split:
 *
 *   LIVE view       ← gated by PAID tier alone.
 *                     Free users see DELAYED (first-ever snapshot).
 *                     Paid users can toggle freely, no modal.
 *
 *   Subscribe       ← opt-in for email delivery, orthogonal to view.
 *                     Free users clicking Subscribe → pricing modal.
 *                     Paid users clicking Subscribe → 1-click API call.
 *
 * What lives on the context:
 *   - `view`         the user's current choice ("live" | "delayed")
 *   - `canGoLive`    true if they're allowed to go live (owner / paid / trial / enterprise)
 *   - `setView(v)`   flip view; for free users clicking "live", this
 *                    triggers the paywall instead of actually switching
 *   - `isSubscribed` / `subscribe()` / `unsubscribe()`
 *                    Subscribe toggles as a 1-click for paid users,
 *                    or opens the pricing modal for free users.
 *   - `openPaywall(reason)`
 *                    Low-level direct trigger, consumed by expand-cell
 *                    clicks so they can pop the right flow too.
 *
 * Shape chosen so that AlertCanvas, AlertCellNode, and AlertTopBarPublic
 * can all consume this SAME context without threading state through
 * Chat→Artifact→Canvas.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import useSWR, { useSWRConfig } from "swr";
import { useAuth } from "@/contexts/AuthContext";
import { useUserUsage } from "@/hooks/useUserUsage";
import { AuthModal } from "@/components/auth/auth-modal";
import PlansModal from "@/components/plans-modal";
import { apiClient, type AlertDoc, type AlertSnapshot } from "@/lib/api-client";
import { SUBSCRIBED_SWR_KEY } from "@/components/sidebar-alerts";
import { telemetry } from "@/lib/telemetry";
import { markRecentlySubscribed } from "@/lib/recently-subscribed";

/** Shape of the cached `alerts:subscribed` SWR payload (list + count). */
type SubscribedCache = { alerts: AlertDoc[]; count: number } | undefined;

// Tier names that count as "paid" — mirrors backend VALID_PAID_TIERS in
// app/core/plan_tiers.py. Duplicated client-side for synchronous click
// decisions (no round-trip to decide what modal to open).
const PAID_TIERS = new Set(["plus", "pro", "elite"]);

type ViewMode = "live" | "delayed";
type PaywallReason = "live" | "subscribe" | "manage";

/**
 * Short relative-age formatter for the DELAYED chip. Tight formatting
 * (1-2 chars + unit) so the toggle doesn't wrap on narrow viewports.
 * "5m", "3h", "5d", "3w". Older than 8 weeks → "3mo" / "1y".
 */
function formatRelativeAge(iso: string): string {
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "";
  const diffSec = Math.max(1, Math.floor((Date.now() - t) / 1000));
  if (diffSec < 60) return `${diffSec}s`;
  const m = Math.floor(diffSec / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  const d = Math.floor(h / 24);
  if (d < 14) return `${d}d`;
  const w = Math.floor(d / 7);
  if (w < 9) return `${w}w`;
  const mo = Math.floor(d / 30);
  if (mo < 12) return `${mo}mo`;
  return `${Math.floor(d / 365)}y`;
}

interface AlertPublicViewContextValue {
  // ── View state ────────────────────────────────────────────────────
  view: ViewMode;
  /** True when the caller is allowed to see the LIVE dashboard
   *  (owner, paid tier, enterprise, or active trial). */
  canGoLive: boolean;
  /** Flip view. For free users calling `setView("live")`, this
   *  automatically routes through the paywall instead of switching. */
  setView: (v: ViewMode) => void;
  /** Short relative-time string for the DELAYED snapshot age,
   *  e.g. "5d ago", "3w ago". `null` until the first-snapshot fetch
   *  lands or if none exists yet. */
  delayedAgeLabel: string | null;

  // ── Subscribe (email opt-in) — separate from view ────────────────
  isSubscribed: boolean;
  subscribeBusy: boolean;
  subscribe: () => Promise<void>;
  unsubscribe: () => Promise<void>;

  // ── Low-level paywall trigger ────────────────────────────────────
  openPaywall: (reason: PaywallReason) => void;
}

const AlertPublicViewCtx = createContext<AlertPublicViewContextValue | null>(null);

export function useAlertPublicView(): AlertPublicViewContextValue {
  const ctx = useContext(AlertPublicViewCtx);
  if (!ctx) {
    // Defensive no-op for callers mounted outside the provider (e.g.
    // /chat in alert-mode has no public-view gating).
    return {
      view: "live",
      canGoLive: true,
      setView: () => {},
      delayedAgeLabel: null,
      isSubscribed: false,
      subscribeBusy: false,
      subscribe: async () => {},
      unsubscribe: async () => {},
      openPaywall: () => {},
    };
  }
  return ctx;
}

/**
 * Back-compat alias — existing components still call `useAlertPaywall()`.
 * Re-exports the subset of the new context that those callers use.
 */
export function useAlertPaywall(): { open: () => void } {
  const ctx = useAlertPublicView();
  return { open: () => ctx.openPaywall("live") };
}

interface AlertPublicViewProviderProps {
  alert: AlertDoc;
  /** True when the viewer owns this alert. Owners always see LIVE —
   *  free-tier users can now build one alert, so "owner ⟹ paid" no
   *  longer holds and ownership must bypass the paid gate explicitly. */
  isOwner?: boolean;
  initialIsSubscribed: boolean;
  /** Email to pre-fill on the subscribe path (currently unused for
   *  1-click subscribes, but surfaced so future "change email" flows
   *  can resurrect it). */
  defaultEmail?: string;
  /** Called after any state-changing action (subscribe/unsubscribe)
   *  so the page can re-fetch the alert doc. */
  onMutated: () => void;
  children: ReactNode;
}

export function AlertPublicViewProvider({
  alert,
  isOwner = false,
  initialIsSubscribed,
  defaultEmail,
  onMutated,
  children,
}: AlertPublicViewProviderProps) {
  const { user, loading: authLoading } = useAuth();
  const { data: usage, isLoading: usageLoading } = useUserUsage();
  // Used to refresh the sidebar's "Subscribed Alerts" list (and its
  // section-visibility gate) the instant a subscribe/unsubscribe lands, so a
  // newly-subscribed signal/alert pops up without a page reload.
  const { mutate: globalMutate } = useSWRConfig();

  // Compute paid-tier membership. Mirrors backend _is_user_paid() in
  // alerts.py. Free users = default to DELAYED + see paywall on "live".
  const entitlementPending = Boolean(user) && (authLoading || (usageLoading && !usage));
  const tier = usage?.subscription?.tier ?? "free";
  const onActiveTrial = Boolean(usage?.free_trial?.active);
  const enterprise = Boolean(usage?.is_enterprise);
  const confirmedCanGoLive = isOwner || PAID_TIERS.has(tier) || onActiveTrial || enterprise;
  // While usage is loading, treat signed-in users as potentially paid so
  // paid users do not get stuck on the delayed view from a temporary free default.
  const canGoLive = entitlementPending || confirmedCanGoLive;

  // Default view: paid goes LIVE, free stays DELAYED. User can override
  // via the toggle. We keep this as useState so a paid user who
  // explicitly picked DELAYED isn't auto-flipped back to LIVE when the
  // usage data refetches.
  const userSelectedViewRef = useRef(false);
  const [view, _setView] = useState<ViewMode>(user ? "live" : "delayed");
  useEffect(() => {
    if (!user) {
      _setView("delayed");
      return;
    }
    if (!entitlementPending && !confirmedCanGoLive && view === "live") {
      userSelectedViewRef.current = false;
      _setView("delayed");
      return;
    }
    if (userSelectedViewRef.current) return;
    if (!entitlementPending) {
      _setView(confirmedCanGoLive ? "live" : "delayed");
    }
  }, [user, entitlementPending, confirmedCanGoLive, view]);

  // Track subscription state locally so optimistic flips don't require a
  // round-trip to the backend before the UI reflects the new state.
  const [isSubscribed, setIsSubscribed] = useState(initialIsSubscribed);
  useEffect(() => {
    setIsSubscribed(initialIsSubscribed);
  }, [initialIsSubscribed]);
  const [subscribeBusy, setSubscribeBusy] = useState(false);

  // Which modal is open. Only one can be visible at a time.
  const [authOpen, setAuthOpen] = useState(false);
  const [plansOpen, setPlansOpen] = useState(false);

  // ─── Stranger auth gate ──────────────────────────────────────────
  // Signed-out visitors to /alerts/{id} get the AuthModal force-opened
  // immediately — not waiting for them to click something gated. The
  // AuthModal's backdrop (black + blur) doubles as the page overlay,
  // so the alert canvas is visible-but-muted underneath (teaser intent
  // preserved, forced signup on top).
  //
  // We don't render a separate overlay component — the existing
  // portal-based AuthModal backdrop already covers the whole viewport.
  // Keeping this change to 3 useEffect lines vs. a new component.
  useEffect(() => {
    if (!user) {
      setAuthOpen(true);
    } else {
      // Auto-close once they finish signup — `useAuth().user` flips
      // truthy inside AuthModal's own success handlers.
      setAuthOpen(false);
    }
  }, [user]);

  const openPaywall = useCallback(
    (reason: PaywallReason) => {
      // No user → always signup first. Post-signup they're on the free
      // tier, so clicking the same control again opens the pricing modal.
      // Two-step is intentional; one-step would require chained modals.
      if (!user) {
        console.info("[public-view] paywall open — unauthenticated", { reason });
        setAuthOpen(true);
        return;
      }
      if (!canGoLive) {
        console.info("[public-view] paywall open — signed-in free tier", { reason });
        setPlansOpen(true);
        return;
      }
      // Paid user triggered a paywall? Shouldn't happen through normal
      // clicks because `canGoLive === true` lets them pass every gate.
      // Swallow safely — log for telemetry only.
      console.info("[public-view] paywall suppressed — paid user", { reason });
    },
    [user, canGoLive],
  );

  const setView = useCallback(
    (v: ViewMode) => {
      if (v === "live" && !canGoLive) {
        // Free user tried to switch to LIVE. Route through the paywall
        // rather than silently refusing so they actually see an upgrade path.
        openPaywall("live");
        return;
      }
      console.info("[public-view] setView", { from: view, to: v, canGoLive });
      userSelectedViewRef.current = true;
      _setView(v);
    },
    [canGoLive, openPaywall, view],
  );

  const subscribe = useCallback(async () => {
    // Free users fall into the paywall. Paid users subscribe with one
    // click using their Firebase account email — no modal, no email
    // confirmation. If they ever need a different delivery email we'll
    // add a "change email" link in the Subscribed dropdown.
    if (!user) {
      openPaywall("subscribe");
      return;
    }
    if (!canGoLive) {
      openPaywall("subscribe");
      return;
    }
    setSubscribeBusy(true);
    const alertKind = (alert as { kind?: string })?.kind ?? "alert";
    telemetry.emit("alert.subscribe_click", { alert_id: alert._id, kind: alertKind });
    try {
      const email = defaultEmail || user.email || undefined;
      const res = await apiClient.subscribeAlert(alert._id, email);
      console.info("[public-view] subscribed", { alertId: alert._id, email: res.email });
      setIsSubscribed(true);
      onMutated();

      // ── Instant pop (optimistic insert) ──────────────────────────────────
      // Write the new row straight into the sidebar's cached subscribed list
      // INSTEAD of only firing a network refetch. This makes the row appear
      // with zero network wait and — because the section-visibility gate in
      // app-sidebar reads the same key's `count` — instantly reveals the
      // "Subscribed Alerts" section on a user's first subscription. We then
      // revalidate in the background so the server stays the source of truth.
      // Flag this id BEFORE the cache write so that when the sidebar's
      // "Subscribed Alerts" section mounts for the first time (it's hidden at
      // count 0), the freshly-mounted list still knows this row is brand new
      // and plays the pop+shake — a plain prev/next diff can't see it there.
      markRecentlySubscribed(alert._id);
      let optimisticInserted = false;
      globalMutate(
        SUBSCRIBED_SWR_KEY,
        (current: SubscribedCache): SubscribedCache => {
          const list = current?.alerts ?? [];
          // Never duplicate: if a background fetch already added it, no-op.
          if (list.some((a) => a._id === alert._id)) return current;
          optimisticInserted = true;
          // Shape mirrors GET /alerts/subscribed rows: minimal projection,
          // explicitly non-owner + subscribed. AlertItem only reads
          // _id/name/status/kind so the full AlertDoc spread is safe.
          const optimisticRow: AlertDoc = { ...alert, is_subscribed: true, is_owner: false };
          return { alerts: [optimisticRow, ...list], count: (current?.count ?? 0) + 1 };
        },
        { revalidate: true },
      );
      telemetry.emit("alert.subscribe_success", {
        alert_id: alert._id,
        kind: alertKind,
        optimistic_inserted: optimisticInserted,
      });
    } catch (err) {
      console.error("[public-view] subscribe failed", { err });
      telemetry.emit("alert.subscribe_error", {
        alert_id: alert._id,
        kind: alertKind,
        error_message: String((err as Error)?.message ?? err).slice(0, 300),
      });
    } finally {
      setSubscribeBusy(false);
    }
  }, [user, canGoLive, defaultEmail, alert, openPaywall, onMutated, globalMutate]);

  const unsubscribe = useCallback(async () => {
    setSubscribeBusy(true);
    try {
      await apiClient.unsubscribeAlert(alert._id);
      console.info("[public-view] unsubscribed", { alertId: alert._id });
      setIsSubscribed(false);
      onMutated();
      // Optimistic remove so the row disappears instantly, then revalidate.
      globalMutate(
        SUBSCRIBED_SWR_KEY,
        (current: SubscribedCache): SubscribedCache => {
          if (!current) return current;
          return {
            alerts: current.alerts.filter((a) => a._id !== alert._id),
            count: Math.max(0, current.count - 1),
          };
        },
        { revalidate: true },
      );
      telemetry.emit("alert.unsubscribe_success", {
        alert_id: alert._id,
        kind: (alert as { kind?: string })?.kind ?? "alert",
      });
    } catch (err) {
      console.error("[public-view] unsubscribe failed", { err });
      telemetry.emit("alert.subscribe_error", {
        alert_id: alert._id,
        phase: "unsubscribe",
        error_message: String((err as Error)?.message ?? err).slice(0, 300),
      });
    } finally {
      setSubscribeBusy(false);
    }
  }, [alert, onMutated, globalMutate]);

  // ── DELAYED snapshot age fetch ───────────────────────────────────
  // Fetch /snapshots/first once so the toggle can render "(5d)" next to
  // DELAYED. SWR dedupes by key with AlertCanvas's own fetch of the
  // same endpoint, so this is one network round-trip, not two. We
  // scope the cache key to alert.has_snapshots so new alerts don't
  // spam 404s at the backend before any run has captured.
  const firstSnapKey = alert.has_snapshots && alert._id
    ? `alert-first-snap-${alert._id}`
    : null;
  const { data: firstSnap } = useSWR<{ snapshot: AlertSnapshot } | null>(
    firstSnapKey,
    async () => {
      try {
        return await apiClient.getAlertSnapshotFirst(alert._id);
      } catch {
        // 404 for alerts that haven't captured yet is expected — swallow.
        return null;
      }
    },
    {
      revalidateOnFocus: false,
      dedupingInterval: 30_000,
      shouldRetryOnError: false,
    },
  );
  const delayedAgeLabel = useMemo(() => {
    const runAt = firstSnap?.snapshot?.run_at;
    if (!runAt) return null;
    return formatRelativeAge(runAt);
  }, [firstSnap]);

  const value = useMemo<AlertPublicViewContextValue>(
    () => ({
      view,
      canGoLive,
      setView,
      delayedAgeLabel,
      isSubscribed,
      subscribeBusy,
      subscribe,
      unsubscribe,
      openPaywall,
    }),
    [view, canGoLive, setView, delayedAgeLabel, isSubscribed, subscribeBusy, subscribe, unsubscribe, openPaywall],
  );

  // Props PlansModal needs to render the correct pricing lane.
  const stripeAccount =
    (usage?.stripe_account as "new" | "old" | null | undefined) ?? null;
  const currentPlan = (usage?.subscription?.tier ?? "free") as
    | "free"
    | "plus"
    | "pro"
    | "elite";

  return (
    <AlertPublicViewCtx.Provider value={value}>
      {children}

      <AuthModal
        open={authOpen}
        // For signed-out strangers on /alerts we suppress the close so
        // clicking the backdrop or the internal ✕ doesn't let them skip
        // auth. Successful signup flips `user` truthy, which the effect
        // above picks up and closes the modal programmatically. Once
        // they're signed in, normal close works (they might have tapped
        // Subscribe while already authed, hit "Log in as someone else",
        // etc).
        onClose={() => {
          if (!user) return;
          setAuthOpen(false);
        }}
      />
      <PlansModal
        isOpen={plansOpen}
        onClose={() => setPlansOpen(false)}
        currentPlan={currentPlan}
        stripeAccount={stripeAccount}
        isEnterprise={Boolean(usage?.is_enterprise)}
      />
    </AlertPublicViewCtx.Provider>
  );
}

/**
 * Back-compat alias for the old provider name. Matches the previous
 * AlertPaywallProvider signature 1:1 so page.tsx callers don't break
 * during the rename rollout.
 */
export const AlertPaywallProvider = ({
  alert,
  isSubscribed,
  defaultEmail,
  onMutated,
  children,
}: {
  alert: AlertDoc;
  isSubscribed: boolean;
  defaultEmail?: string;
  onMutated: () => void;
  children: ReactNode;
}) => (
  <AlertPublicViewProvider
    alert={alert}
    initialIsSubscribed={isSubscribed}
    defaultEmail={defaultEmail}
    onMutated={onMutated}
  >
    {children}
  </AlertPublicViewProvider>
);
